import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";

import { businessPartnerApi, fuelPriceApi } from "../../../../api/contractManagementApi";
import { DEFAULT_PAGE_SIZE } from "../constants";
import {
    buildFuelPriceStats,
    buildPricePeriodQueryParams,
    createDefaultWorkbenchFilters,
    getFuelPriceStatusOptions,
    unwrapApiResponse,
} from "../services/fuelPriceWorkbenchViewModel";

/**
 * 燃油价格工作台的数据接入层。
 * 负责周期主表、周期详情、审计日志与周期级写操作的统一编排。
 */
function useFuelPriceWorkbenchData() {
    const [periods, setPeriods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(createDefaultWorkbenchFilters);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        total: 0,
    });
    const [vendors, setVendors] = useState([]);

    const [createLoading, setCreateLoading] = useState(false);
    const [priceInputLoading, setPriceInputLoading] = useState(false);
    const [disputeLoading, setDisputeLoading] = useState(false);

    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsPagination, setLogsPagination] = useState({ current: 1, pageSize: 20, total: 0 });

    const [detailPeriod, setDetailPeriod] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailLogs, setDetailLogs] = useState([]);
    const [detailLogsLoading, setDetailLogsLoading] = useState(false);

    const filtersRef = useRef(filters);
    const paginationRef = useRef(pagination);

    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    useEffect(() => {
        paginationRef.current = pagination;
    }, [pagination]);

    const loadPeriods = useCallback(async ({
        page = paginationRef.current.current,
        pageSize = paginationRef.current.pageSize,
        nextFilters,
    } = {}) => {
        const effectiveFilters = nextFilters ?? filtersRef.current;
        setLoading(true);

        try {
            const response = await fuelPriceApi.getPricePeriods(
                buildPricePeriodQueryParams(effectiveFilters, page, pageSize)
            );
            const data = unwrapApiResponse(response);

            setPeriods(data.items || []);
            setPagination({
                current: data.page || page,
                pageSize: data.page_size || pageSize,
                total: data.total || 0,
            });
        } catch (error) {
            message.error("加载价格周期主表失败");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadVendors = useCallback(async () => {
        try {
            const response = await businessPartnerApi.getSuppliers({ page_size: 500 });
            const data = unwrapApiResponse(response);
            setVendors(data.items || data || []);
        } catch (error) {
            console.error("加载供应商列表失败:", error);
        }
    }, []);

    const loadWorkbenchLogs = useCallback(async (page = 1) => {
        setLogsLoading(true);

        try {
            const response = await fuelPriceApi.getOperationLogs({
                page,
                page_size: 20,
                target_type: "PRICE_PERIOD",
            });
            const data = unwrapApiResponse(response);

            setLogs(data.items || []);
            setLogsPagination({
                current: data.page || page,
                pageSize: data.page_size || 20,
                total: data.total || 0,
            });
        } catch (error) {
            message.error("加载工作台操作日志失败");
            console.error(error);
        } finally {
            setLogsLoading(false);
        }
    }, []);

    const loadDetailLogs = useCallback(async (periodId) => {
        setDetailLogsLoading(true);

        try {
            const response = await fuelPriceApi.getOperationLogs({
                page: 1,
                page_size: 20,
                target_type: "PRICE_PERIOD",
                target_id: periodId,
            });
            const data = unwrapApiResponse(response);
            setDetailLogs(data.items || []);
        } catch (error) {
            message.error("加载周期操作日志失败");
            console.error(error);
        } finally {
            setDetailLogsLoading(false);
        }
    }, []);

    const loadDetailPeriod = useCallback(async (periodId) => {
        setDetailLoading(true);

        try {
            const response = await fuelPriceApi.getPricePeriodById(periodId);
            const data = unwrapApiResponse(response);
            setDetailPeriod(data);
        } catch (error) {
            message.error(error.response?.data?.detail || "加载周期详情失败");
            console.error(error);
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const loadDetailWorkspace = useCallback(
        async (periodId) => {
            await Promise.all([loadDetailPeriod(periodId), loadDetailLogs(periodId)]);
        },
        [loadDetailLogs, loadDetailPeriod]
    );

    const clearDetailWorkspace = useCallback(() => {
        setDetailPeriod(null);
        setDetailLogs([]);
    }, []);

    const refreshPeriodRelatedViews = useCallback(
        async (periodId) => {
            await loadPeriods();

            if (detailPeriod?.id === periodId) {
                await loadDetailWorkspace(periodId);
            }
        },
        [detailPeriod?.id, loadDetailWorkspace, loadPeriods]
    );

    useEffect(() => {
        loadPeriods({ page: 1, pageSize: DEFAULT_PAGE_SIZE, nextFilters: filtersRef.current });
        loadVendors();
    }, [loadPeriods, loadVendors]);

    const searchPeriods = useCallback(() => {
        loadPeriods({ page: 1, pageSize: paginationRef.current.pageSize });
    }, [loadPeriods]);

    const resetFilters = useCallback(() => {
        const nextFilters = createDefaultWorkbenchFilters();
        setFilters(nextFilters);
        loadPeriods({ page: 1, pageSize: paginationRef.current.pageSize, nextFilters });
    }, [loadPeriods]);

    const handleTableChange = useCallback(
        (nextPagination) => {
            loadPeriods({ page: nextPagination.current, pageSize: nextPagination.pageSize });
        },
        [loadPeriods]
    );

    const createPricePeriod = useCallback(
        async (payload) => {
            setCreateLoading(true);

            try {
                await fuelPriceApi.createPricePeriod(payload);
                message.success("创建价格周期成功");
                await loadPeriods({ page: 1, pageSize: paginationRef.current.pageSize });
                return true;
            } catch (error) {
                message.error(error.response?.data?.detail || "创建失败");
                return false;
            } finally {
                setCreateLoading(false);
            }
        },
        [loadPeriods]
    );

    const updateOurPrice = useCallback(
        async (periodId, price) => {
            setPriceInputLoading(true);

            try {
                await fuelPriceApi.updateOurPrice(periodId, price);
                message.success("我方参考快照已更新");
                await refreshPeriodRelatedViews(periodId);
                return true;
            } catch (error) {
                message.error(error.response?.data?.detail || "更新失败");
                return false;
            } finally {
                setPriceInputLoading(false);
            }
        },
        [refreshPeriodRelatedViews]
    );

    const updateSupplierQuote = useCallback(
        async (periodId, price) => {
            setPriceInputLoading(true);

            try {
                await fuelPriceApi.updateSupplierQuote(periodId, {
                    price,
                    price_source: "MANUAL_INPUT",
                });
                message.success("供应商报价快照已录入");
                await refreshPeriodRelatedViews(periodId);
                return true;
            } catch (error) {
                message.error(error.response?.data?.detail || "录入失败");
                return false;
            } finally {
                setPriceInputLoading(false);
            }
        },
        [refreshPeriodRelatedViews]
    );

    const confirmPricePeriod = useCallback(
        async (period) => {
            try {
                await fuelPriceApi.confirmPrice(period.id, {});
                message.success("周期油价已确认");
                await refreshPeriodRelatedViews(period.id);
            } catch (error) {
                message.error(error.response?.data?.detail || "确认失败");
            }
        },
        [refreshPeriodRelatedViews]
    );

    const disputePricePeriod = useCallback(
        async (periodId, reason) => {
            setDisputeLoading(true);

            try {
                await fuelPriceApi.disputePrice(periodId, { reason });
                message.success("已标记为异常周期");
                await refreshPeriodRelatedViews(periodId);
                return true;
            } catch (error) {
                message.error(error.response?.data?.detail || "操作失败");
                return false;
            } finally {
                setDisputeLoading(false);
            }
        },
        [refreshPeriodRelatedViews]
    );

    const deletePricePeriod = useCallback(
        async (period) => {
            try {
                await fuelPriceApi.deletePricePeriod(period.id);
                message.success("周期记录已删除");
                await loadPeriods();

                if (detailPeriod?.id === period.id) {
                    clearDetailWorkspace();
                }
            } catch (error) {
                message.error(error.response?.data?.detail || "删除失败");
            }
        },
        [clearDetailWorkspace, detailPeriod?.id, loadPeriods]
    );

    const periodMap = useMemo(
        () => new Map(periods.map((period) => [period.id, period])),
        [periods]
    );

    const getPeriodById = useCallback(
        (periodId) => {
            if (!periodId) {
                return null;
            }

            if (detailPeriod?.id === periodId) {
                return detailPeriod;
            }

            return periodMap.get(periodId) || null;
        },
        [detailPeriod, periodMap]
    );

    return {
        periods,
        loading,
        pagination,
        filters,
        setFilters,
        vendors,
        stats: buildFuelPriceStats(periods, pagination.total),
        statusOptions: getFuelPriceStatusOptions(),
        searchPeriods,
        resetFilters,
        handleTableChange,
        createPricePeriod,
        createLoading,
        updateOurPrice,
        updateSupplierQuote,
        priceInputLoading,
        confirmPricePeriod,
        disputePricePeriod,
        disputeLoading,
        deletePricePeriod,
        logs,
        logsLoading,
        logsPagination,
        loadWorkbenchLogs,
        detailPeriod,
        detailLoading,
        detailLogs,
        detailLogsLoading,
        loadDetailWorkspace,
        clearDetailWorkspace,
        getPeriodById,
    };
}

export default useFuelPriceWorkbenchData;
